// İSG Portal — İş İzni (Permit-to-Work — tenant-scoped) API çağrıları + tipleri.
// **İSG uzmanı tarafı** (web-expert): izin CRUD + yaşam döngüsü (submit/approve/reject/
// close) + kapsanan personel + gerekli eğitim ataması + personel readiness.
//
// KAYNAK: api-contract.md §14 (İş İzni) + §17.2 (gerekli eğitim atama + önkoşul gate).
// Backend kanonu; burada **varsayım YOK** — endpoint yolları, istek/yanıt alanları ve
// kimlik kuralları (Result unwrap `value`; Create → `value.uuid` + 201; liste → `value`
// + `total`/`filteredTotal`) doğrudan sözleşmeden türetilmiştir. Serileştirme camelCase (§1).
//
// **Tenant izolasyonu (§4b/§14).** Tüm uçlar çağıranın firmasına (token `CompanyId`)
// kapanır; istemci firma kimliği GÖNDERMEZ — sunucu token'dan çözer. Başka firmanın
// izni/personeli hiçbir koşulda görünmez (yok gibi `404 PERMIT_NOT_FOUND`). Silme
// **yoktur** (terminal durum = `Closed`/`Rejected`); personel kapsam bağı soft-delete.
//
// **Türetilmiş durum (saklanmaz, §14).** `validityEnd` geçmiş bir `Approved`/`Active` izin
// liste/get'te `Expired` olarak türetilir; istemci status'u yalnız gösterir (yeniden
// hesaplamaz). Yaşam döngüsü + düzenlenebilirlik durumu backend otoritesindedir.

import { api } from './client';
import type { ComplianceStatus, HazardClass, PersonnelDocumentDto } from './contractors';
import type { Empty } from './envelope';
import type { PermitApprovalDepartment } from './permitApprovalRouting';
import type { ProtectiveEquipmentRefDto } from './protectiveEquipment';

/**
 * İzin durumu — wire değerleri PascalCase (§14 `WorkPermitDto.status`). `Expired`
 * **türetilir** (saklanmaz): `validityEnd` geçmiş açık izin.
 *
 * **Faz B onay akışı (SPEC §1 — brief 093 durum makinesi genişletmesi).** Statüler
 * additive olarak eklendi; mevcut `Approved` semantik olarak `AwaitingSupplierInfo`'ya
 * (onay sonrası tedarikçi bilgisi beklenir) karşılık gelir — backend eski `Approved`'ı
 * yeniden yorumlar. FE her iki değeri de tanır (geçiş dönemi verisi için `Approved`
 * union'da bırakıldı). Akış:
 * `Draft` → `Submitted` → `AwaitingSupplierInfo` → `AwaitingAiReview` →
 * `DepartmentReview` → `AwaitingEntryPermit` → `Active`; her aşamada `Revision` (revize)
 * veya `Rejected` (terminal) mümkün; `Closed`/`Expired` mevcut.
 */
export type PermitStatus =
  | 'Draft'
  | 'Submitted'
  | 'Approved'
  | 'AwaitingSupplierInfo'
  | 'AwaitingAiReview'
  | 'Revision'
  | 'DepartmentReview'
  | 'AwaitingEntryPermit'
  | 'Active'
  | 'Rejected'
  | 'Expired'
  | 'Closed';

/**
 * İznin kapsadığı bir saha — api-contract §14 (`WorkPermitSiteDto`). **Çoklu lokasyon (§14
 * Faz 2)**: bir izin birden çok sahayı kapsayabilir (eski tekil `siteUuid?`/`siteName?` yerine).
 * `uuid` = sahanın (`core."Site"`) public kimliği; `name` tenant çekirdeğinden çözülür (ada göre
 * sıralı gelir). Saha seçimi opsiyonel olduğundan iznin `sites` dizisi `[]` olabilir.
 */
export interface WorkPermitSiteDto {
  uuid: string;
  name: string;
}

/**
 * İş İzni — api-contract §14 (`WorkPermitDto`). Sıralı iç `Id` istemciye **sızmaz** (yalnız
 * `uuid`). `validityStart`/`validityEnd`/`approvedAt`/`createdAt` UTC ISO (§4; DateTime).
 * `hazardClass` sayısal (1=Az / 2=Tehlikeli / 3=Çok Tehlikeli); `status` PascalCase.
 * `permitTypeUuid`/`permitTypeName` = iznin **firma-tanımlı tipi** (§22; eski `workType` yerine —
 * sahip kararı 2026-08-11). Tip soft-delete edilmiş olsa da `permitTypeName` çözülmeye devam eder.
 * `contractorName`/`sites[].name`/`approvedByName`/`permitTypeName` ilgili store'lardan çözülür;
 * `personnelCount` = kapsanan aktif personel sayısı. `sites` = iznin kapsadığı sahalar
 * (**çoklu lokasyon — §14 Faz 2**; eski tekil `siteUuid?`/`siteName?` yerine; `[]` olabilir).
 */
export interface WorkPermitDto {
  uuid: string;
  contractorUuid: string;
  contractorName: string | null;
  /** İznin kapsadığı sahalar — ada göre sıralı; saha seçimi opsiyonel olduğundan `[]` olabilir (§14 Faz 2). */
  sites: WorkPermitSiteDto[];
  /** İznin firma-tanımlı tipi (§22). */
  permitTypeUuid: string;
  /** Tipin adı (soft-delete edilmiş tip için de çözülür; §22). */
  permitTypeName: string | null;
  title: string;
  description: string | null;
  hazardClass: HazardClass;
  /**
   * İzinde araç olacak mı? — form "araç olacak mı?" bayrağı (§23 Faz 3a). Yalnız gösterge/form
   * ipucudur; araç kapsamı tek kaynak `vehicles`'tir (detay; §23). Create/update isteğinde set edilir.
   */
  hasVehicles: boolean;
  /** Geçerlilik başlangıcı — UTC ISO (§4; DateTime). */
  validityStart: string;
  /** Geçerlilik bitişi — UTC ISO (§4; DateTime). Türetilmiş `Expired`'ın kaynağı. */
  validityEnd: string;
  status: PermitStatus;
  approvedByName: string | null;
  /** Onay anı — UTC ISO (§4); onaylanmışsa dolu. */
  approvedAt: string | null;
  personnelCount: number;
  createdAt: string | null;
}

/**
 * Gerekli eğitim (izin önkoşulu) — api-contract §17.2 (`RequiredTrainingDto`). `trainingUuid`
 * = eğitimin (`Training`) public kimliği; `title` eğitim shard'ından çözülür.
 */
export interface RequiredTrainingDto {
  trainingUuid: string;
  title: string;
}

/**
 * Araç evrakı onay durumu — api-contract §23 (`VehicleDocumentDto.approvalStatus`, PascalCase
 * wire; DB'de string). Yeni belge `Pending` başlar (taşeron Yetkilisi yükler); İSG uzmanı belgeyi
 * `Approved`/`Rejected` yapar (personel belge onay deseninin muadili; §15.4). Bu durum araç
 * readiness'inin (`vehicleReady`/`missingVehicleDocumentTypes`) girdisidir.
 */
export type VehicleDocumentApprovalStatus = 'Pending' | 'Approved' | 'Rejected';

/**
 * Araç evrakı — api-contract §23 (`VehicleDocumentDto`). Her kayıt §8 Storage'daki **confirmed**
 * bir dosyaya (`documentUuid`) mantıksal referanstır; `fileName` §8'den çözülür (çözülemezse `null`).
 * `type` = `vehicle` entity'sinin §27 kayıt kodu (etiket §27 kaydından). `approvalStatus` (Pending başlar)
 * + `approvedByName`/`approvedAt` (onay/red verilmişse) + `rejectionReason` (yalnız `Rejected`).
 * Onay/red **yalnız İSG uzmanı** (web-expert; §23.1). Sıralı iç `Id` **sızmaz**.
 */
export interface VehicleDocumentDto {
  uuid: string;
  type: string;
  documentUuid: string;
  /** §8 dokümanından çözülen özgün dosya adı (indirme için); çözülemezse `null`. */
  fileName: string | null;
  /** Geçerlilik başlangıcı — UTC ISO (§4; DateTime). */
  validFrom: string | null;
  /** Geçerlilik bitişi — UTC ISO (§4; DateTime). */
  validUntil: string | null;
  /**
   * Gösterim durumu, **türün `expiryRule`'una göre sunucuda** türetilir (§23.0.3):
   * `NoExpiry` her zaman `Valid` (`validUntil` okunmaz); diğer kurallarda `validUntil` ve o anki
   * UTC belirler. İstemci yeniden hesaplamaz; kapı değil gösterimdir.
   */
  validityStatus: ComplianceStatus;
  /** §23.0.4 "kullanımda" olgusu — `true` iken ana firma silmesi `409 VEHICLE_DOCUMENT_IN_USE`. */
  isInUse: boolean;
  note: string | null;
  /** Onay durumu (İSG uzmanı; §23.1) — `Pending` başlar; türetilmez, saklanır. */
  approvalStatus: VehicleDocumentApprovalStatus;
  /** Onaylayan/reddeden İSG uzmanının adı (`Pending` iken `null`). Taşeron yanıtlarında (araç belgeleri) HER ZAMAN `null` — uzman kimliği taşerona gösterilmez. */
  approvedByName: string | null;
  /** Onay/red anı — UTC ISO (§4; DateTime). `Pending` iken `null`. */
  approvedAt: string | null;
  /** Red gerekçesi — yalnız `Rejected`'ta dolu (onay önceki gerekçeyi temizler). */
  rejectionReason: string | null;
  createdAt: string | null;
  /** GL #7 §4.2: bu dosyayı bu (entity, type) olarak doğrulayan doc-check sürümü; `null` = AI doğrulamadı (elle yükleme vb.). Yalnız sunucu yazar. */
  recognitionRelease: number | null;
}

/**
 * İzin aracı — api-contract §23 (`WorkPermitVehicleDto`). `uuid` = **KAYIT aracı** (§23.0.1, KIRICI);
 * `createdAt` = izne bağlanma anı. Bir plaka + evrak seti (`documents`).
 * `vehicleReady` = iznin permit tipinde istenen **tüm** araç belge türleri present + `Approved`
 * (tipte istenen araç belgesi yoksa trivially `true`); `missingVehicleDocumentTypes` = istenen ama
 * araçta `Approved` olmayan araç belge türlerinin §27 `vehicle` kayıt kodları (kayıt sırası §27.0(5);
 * etiket §27 kaydından). **Personel giriş-gate'inden AYRI** bir göstergedir (§23). Tümü sunucuda
 * türetilir/okunur — istemci yeniden hesaplamaz. Sıralı iç `Id` **sızmaz** (yalnız `uuid`).
 */
export interface WorkPermitVehicleDto {
  uuid: string;
  plate: string;
  description: string | null;
  /** İstenen tüm araç belge türleri present + `Approved` VE geçerli (§23.0.3; türetilir): süresi dolmuş belge, ya da tarihsiz Sigorta/Muayene, eksik sayılır. */
  vehicleReady: boolean;
  /** İstenen ama araçta `Approved` ve geçerli olmayan araç belge türleri — §27 `vehicle` kayıt kodları (§23.0.3; süresi dolmuş ya da tarihsiz Sigorta/Muayene dahil; türetilir). */
  missingVehicleDocumentTypes: string[];
  /** Kayıt aracının plaka kilidi olgusu (§23.0.7) — ana firmanın izin ekranından düzenlemesi için. */
  isPlateLocked: boolean;
  /** Aracın evrak seti — her biri onay durumuyla (§23). */
  documents: VehicleDocumentDto[];
  createdAt: string | null;
}

/** §30.10 — istenen kümenin bir üyesi (tip türleri ∪ izne özel talepler), enum sırası. */
export interface RequestedDocumentDto {
  documentType: string; // §27 `personnel` kayıt kodu
  note: string | null; // yalnız izne özel talepten (§30.10)
  satisfied: boolean; // false <=> documentType ∈ missingDocumentTypes
}

/**
 * İzin kapsamındaki personel — api-contract §14/§17.2 (`WorkPermitPersonnelDto`). `uuid` =
 * personelin (`SupplierUser`) public kimliği (kapsamdan çıkarma bununla). `emailOrPhone` =
 * iletişim/giriş hedefi (e-posta öncelikli). **Eğitim önkoşul durumu (§17, türetilir):**
 * `requiredTrainingCount` = iznin gerekli eğitim sayısı (tüm personel için aynı);
 * `completedTrainingCount` = bu personelin geçtiği gerekli eğitim sayısı;
 * `missingTrainings` = geçmediği HER gerekli eğitim (§35.3, YENİ — başlık dâhil, `[]` ASLA `null`).
 *
 * **Belge önkoşul durumu (§15.4/§22 **tip-bazlı** + §30.10 ek talepler, türetilir):** iznin
 * istenen belge kümesine göre — `requiredDocumentCount` = istenen birleşik kümenin tür sayısı (tip
 * `requiredDocuments` ∪ §30.10 ek talepler; tüm personel için aynı; `requestedDocuments.length`'e
 * eşit; ek talep varken `requiredDocuments.length`'ten büyük olabilir); `satisfiedDocumentCount` = bu
 * personelin onaylı (`Approved`) belgeyle sahip olduğu istenen tür sayısı;
 * `requiredDocumentsSatisfied` = **istenen tüm türler present + `Approved`** (`missingDocumentTypes`
 * boş); `missingDocumentTypes` = istenen ama personelde onaylı olmayan belge türlerinin wire
 * kodları (§27 `personnel`; kayıt sırası §27.0(5); etiket §27 kaydından); `requestedDocuments` = aynı
 * kümenin tür başına görünümü (`RequestedDocumentDto`). **Kenar:** istenen küme boşsa
 * `requiredDocumentCount = 0` ve `requiredDocumentsSatisfied = true` (belge-gate trivially geçer). **Birleşik hazırlık:** `ready` = `requiredDocumentsSatisfied` VE tüm gerekli
 * eğitimler geçildi (`completedTrainingCount == requiredTrainingCount`; her iki boyut da istenen yoksa
 * trivially tamam). Yalnız `ready` iken giriş izni verilebilir (§17.2 `grant-entry`; aksi
 * `409 PERSONNEL_NOT_READY`). `entryPermittedAt`/`entryPermittedByName` giriş izni verildiyse doludur
 * (aksi `null`). Tümü sunucuda türetilir/okunur — istemci yeniden hesaplamaz.
 */
export interface WorkPermitPersonnelDto {
  uuid: string;
  firstName: string;
  lastName: string;
  emailOrPhone: string | null;
  /**
   * (#29) Kayıtlı e-posta yoksa `null` (izin personeli daima aktive kişidir, davet satırı değil).
   * `personalDataMasked` ise maskeli. T.C. bu DTO'da yok.
   */
  email: string | null;
  /** (#29) `email` ile aynı kurallar. */
  phone: string | null;
  /** (#29) Sunucu bu satıra maskeleme politikasını uyguladıysa `true`. */
  personalDataMasked: boolean;
  /** GL6 §4.8 — SupplierUser.PasswordHash != null. Kişisel veri değil, asla maskelenmez. */
  activated: boolean;
  /**
   * `requiredDocumentCount` = istenen birleşik kümenin tür sayısı (tip `requiredDocuments` ∪ §30.10
   * ek talepler; tüm personel için aynı; `requestedDocuments.length`'e eşit; ek talep varken
   * `requiredDocuments.length`'ten büyük olabilir). Türetilir.
   */
  requiredDocumentCount: number;
  /** Bu personelin onaylı (`Approved`) belgeyle karşıladığı istenen tür sayısı (§22; türetilir). */
  satisfiedDocumentCount: number;
  /** İstenen tüm türler present + `Approved` (`missingDocumentTypes` boş; §22; türetilir). */
  requiredDocumentsSatisfied: boolean;
  /** İstenen ama onaylı olmayan belge türleri — §27 `personnel` kayıt kodları (§22; türetilir). */
  missingDocumentTypes: string[];
  requiredTrainingCount: number;
  completedTrainingCount: number;
  /**
   * Bu kişinin geçmediği HER gerekli eğitim — api-contract §35.3 (YENİ, S35). ASLA `null`
   * (`completedTrainingCount < requiredTrainingCount` iken de başlığı çözülemeyen/silinmiş bir
   * eğitim atlanabilir; `[]` bu durumda MEŞRUDUR — durum sayılardan okunur, `dashboard.ts`'in
   * `missingTrainingTitles` alanıyla AYNI kural). Sıra `title` ordinal-ignore-case artan.
   */
  missingTrainings: RequiredTrainingDto[];
  /** `requiredDocumentsSatisfied` VE tüm gerekli eğitimler geçildi (§17.2; türetilir). */
  ready: boolean;
  /** Giriş izni verilme anı — UTC ISO (§4); verilmemişse `null`. */
  entryPermittedAt: string | null;
  /** Giriş iznini veren İSG uzmanının adı — verilmemişse `null`; `/supplier/*` yanıtlarında daima `null` (GL #13). */
  entryPermittedByName: string | null;
  /**
   * İstenen birleşik kümenin her türü (tip ∪ §30.10 ek talepler; enum sırasında) — api-contract
   * §30.10 "Supplier expose". Türler ve notlar her satırda AYNIDIR, yalnız `satisfied` değişir.
   */
  requestedDocuments: RequestedDocumentDto[];
  /**
   * GL #13 — kişinin bu izin taşeronu altındaki TÜM canlı belgeleri, her `approvalStatus`, her tür;
   * `createdAt` azalan; ASLA `null`. `GET /permits/{uuid}` (§14.1) yanıtında DOLU; personel ekle
   * (§14.3) ve grant/revoke-entry (§17.2) yanıtlarında YALNIZ çağıran izni GET edebiliyorsa (iznin
   * görünürlüğü VE `permits:get`) dolu, aksi `[]` ("belgesi yok" DEĞİL); **§31.5 departman detayında daima `[]` — "belgesi
   * yok" demek DEĞİLDİR, Kontrol istemcisi bu alanı OKUMAZ.** `/supplier/*` yanıtlarında her
   * belgenin `approvedByName`'i `null`. `documents[].uuid` = §15 `linkUuid` (onay/red anahtarı).
   * Hazırlık alanları (`ready`, `missingDocumentTypes`) bu listeden istemcide YENİDEN HESAPLANMAZ.
   */
  documents: PersonnelDocumentDto[];
}

/**
 * Personelin istenen tek belge türü — api-contract §30.10 (`RequestedDocumentDto`). `documentType`
 * §27 `personnel` kayıt kodu (PascalCase; etiket §27 kaydından); `note` yalnız izne özel ek talepten gelir
 * (tip-standart belgede `null`); `satisfied` = kişinin o türde kapıya sayan belgesi var
 * (`satisfied = false` ⇔ tür `missingDocumentTypes`'ta). Not uzmanın serbest metnidir — yalnız metin.
 */
export interface RequestedDocumentDto {
  documentType: string;
  note: string | null;
  satisfied: boolean;
}

/**
 * İzin kontrol itemı (checklist satırı — expert) — api-contract §25.3 (`WorkPermitControlItemDto`).
 * Tipin kontrol itemları LEFT JOIN iznin işaretlemesi: işaretlenmemiş item `isChecked=false` + null
 * alanlar. `text` = kontrol ifadesi; `checkedByName` işaretleyen İSG uzmanının adı (identity'den;
 * işaretlenmemişse `null`); `checkedAt` işaretleme anı (UTC ISO — §4; işaretlenmemişse `null`);
 * `note` opsiyonel işaretleme notu. **Yalnız expert** (`GET /permits/{uuid}`) taşır — supplier GÖRMEZ.
 */
export interface WorkPermitControlItemDto {
  uuid: string;
  text: string;
  isChecked: boolean;
  /** İşaretleyen İSG uzmanının adı — işaretlenmemişse `null` (§25.3). */
  checkedByName: string | null;
  /** İşaretleme anı — UTC ISO (§4); işaretlenmemişse `null`. */
  checkedAt: string | null;
  note: string | null;
}

/**
 * Kontrol itemı işaretleme isteği — api-contract §25.3 (`POST /permits/{uuid}/control-checks`).
 * Bir kontrol itemını işaretler (**upsert:** aynı `(izin, item)` için tek aktif satır — tekrar
 * işaretleme mevcut satırı günceller; idempotent). `controlItemUuid` iznin permit tipinde olmalı
 * (değilse `404 CONTROL_ITEM_NOT_FOUND`); izin yok → `404 PERMIT_NOT_FOUND`. `checkedByUserId`
 * (token uzmanı) + `checkedAt` sunucuda set edilir. **Giriş-izni kapısına dahil değildir** (D5).
 */
export interface ControlCheckRequest {
  controlItemUuid: string;
  isChecked: boolean;
  note?: string | null;
}

/**
 * İzne özel **ek belge talebi** — api-contract §30.10 (`WorkPermitDocumentRequestDto`). İznin
 * permit tipinde istenen §22 standart belgelerine **EK** olarak İSG/yetkili tarafından, iznin
 * detayından istenen bir belge türü. Talep **permit-seviyesidir** (izindeki TÜM personele uygulanır)
 * ve **readiness'e katılır** (personelin `missingDocumentTypes`/`requiredDocumentsSatisfied` birleşik
 * kümeden okunur — tek kaynak). Aynı izinde aynı türün **tek aktif** talebi vardır (mükerrer engeli).
 * `uuid` = talebin public kimliği (silme `requestId` = bu); `documentType` = PascalCase wire değeri
 * (`personnel` §27 kayıt `code`'u; etiket kayıttan — kayıtta yoksa ham kod); `note` serbest
 * açıklama (yoksa `null`); `requestedByName` talep edenin görünen adı (çözülemezse `null`);
 * `requestedAt` talebin UTC ISO anı (§4). KVKK: yalnız tür + serbest not — belge içeriği/URL yok.
 */
export interface WorkPermitDocumentRequestDto {
  uuid: string;
  /** İstenen belge türü — `personnel` §27 kayıt `code`'u (pasif ya da bilinmeyen olabilir; düşürülmez). */
  documentType: string;
  /** Serbest açıklama — yoksa `null`. */
  note: string | null;
  /** Talep edenin görünen adı — çözülemezse `null`. */
  requestedByName: string | null;
  /** Talebin anı — UTC ISO (§4). */
  requestedAt: string;
}

/**
 * İzin detayı — api-contract §14 (`WorkPermitDetailDto`, `GET /permits/{uuid}`). İzin +
 * gerekli eğitimler (§17.2) + iznin permit tipinde **istenen belge türleri** (`requiredDocuments`;
 * §22 `entity = personnel`; §27 `personnel` kayıt kodları, kayıt sırası §27.0(5); etiket §27
 * kaydından; istenen belge yoksa `[]`) + kapsanan personel (belge +
 * eğitim önkoşul durumuyla).
 */
export interface WorkPermitDetailDto {
  permit: WorkPermitDto;
  requiredTrainings: RequiredTrainingDto[];
  /** İznin tipinde istenen belge türleri — §27 `personnel` kayıt kodları (§22). */
  requiredDocuments: string[];
  personnel: WorkPermitPersonnelDto[];
  /**
   * İznin permit tipinin istediği **araç** belge türleri — §27 `vehicle` kayıt kodları
   * (§22 `entity = vehicle`; kayıt sırası §27.0(5); etiket §27 kaydından; tipte
   * istenen araç belgesi yoksa `[]`; §23 Faz 3a).
   */
  requiredVehicleDocuments: string[];
  /**
   * İznin araçları (§23) — her biri evrak seti + `vehicleReady` + `missingVehicleDocumentTypes`
   * ile. **Personel giriş-gate'i AYNEN kalır** — araç ayrı göstergedir.
   */
  vehicles: WorkPermitVehicleDto[];
  /**
   * İznin permit tipinde istenen **KKD listesi** — api-contract §24.3 (`requiredProtectiveEquipment`;
   * `ProtectiveEquipmentRefDto[]` = `[{ uuid, name }]`; **read-only** — donanımı taşeron getirir;
   * tipe bağlanmamışsa `[]`).
   */
  requiredProtectiveEquipment: ProtectiveEquipmentRefDto[];
  /**
   * İznin **kontrol checklist**'i — api-contract §25.3 (`controlItems`; `WorkPermitControlItemDto[]`;
   * tipin kontrol itemları LEFT JOIN iznin işaretlemesi; işaretlenmemiş item `isChecked=false`).
   * İSG uzmanı iç checklist'i (**supplier detayında YOK**; D4). Boşsa `[]`.
   */
  controlItems: WorkPermitControlItemDto[];
  /**
   * İzne özel **ek belge talepleri** — api-contract §30.10 (`documentRequests`;
   * `WorkPermitDocumentRequestDto[]`). Tip-standart istenen belgelere (`requiredDocuments`) **EK**;
   * permit-seviyesi (izindeki TÜM personele uygulanır) ve readiness'e katılır (personel
   * `missingDocumentTypes` birleşik kümeden — tek kaynak). Talep yoksa `[]`.
   */
  documentRequests: WorkPermitDocumentRequestDto[];
  /**
   * **Reviewer sinyali (api-contract §30.4 — SPEC §3/§4).** Detay yanıtına eklenen buton-gate
   * ipuçları; **yetki motoru yine 403 ile son kapıdır** — bunlar yalnız UX görünürlüğüdür (403'e
   * güvenmek yerine butonu baştan gizler). Alanlar **opsiyonel**: backend §30 sürümü öncesi (veya
   * eski satırlar) bu alanları döndürmezse `undefined` gelir → ekran mevcut davranışa (status-bazlı
   * gate) düşer, regresyon olmaz (brief 098 İş 3).
   *
   * - `canReview` = çağıran reviewer mı (`permits/approve` grant'ı).
   * - `canApprove` = `canReview` **VE** izin onay/ret yapılabilir durumda (`Submitted`).
   * - `canEdit` = izin düzenlenebilir mi (terminal değil + reviewer her açık aşamada | açan
   *   yalnız `Draft`/`Submitted`).
   */
  canReview?: boolean;
  canApprove?: boolean;
  canEdit?: boolean;
  /** GL6 §4.8 — EP detayında çağıran contractors:update sahibi mi (UX ipucu; 403 son kapı). Eski backend'de undefined gelir → yalnız === true güven. */
  canCreatePersonnel: boolean;
}

/**
 * İzin oluşturma isteği — api-contract §14.1 (`POST /permits`). `contractorUuid`/`siteUuids`/
 * `trainingUuids` çağıranın firmasında doğrulanır (yoksa 404). **Personel açılışta seçilmez**
 * (§14.1 — İSG uzmanı izni açarken kimin geleceğini bilmeyebilir; kapsam sonradan §14.3
 * `POST /permits/{uuid}/personnel` ile eklenir). `siteUuids` opsiyonel (**çoklu lokasyon —
 * §14 Faz 2**; boş/atlanabilir): verilen her saha aynı firmada olmalı (herhangi biri yabancı →
 * `404 SITE_NOT_FOUND`; **kısmi yazma yok**); tekrarlar backend'de tekilleştirilir.
 * `trainingUuids` verilirse her biri aynı firmada + **aktif** bir eğitim olmalı (§17.2; aksi
 * `404 TRAINING_NOT_FOUND` — kısmi yazma yok, izin de kurulmaz). `permitTypeUuid` çağıranın
 * firmasında **aktif** bir tip olmalı (yok/pasif/başka firma → `404 PERMIT_TYPE_NOT_FOUND`; §22),
 * `title` zorunlu, `hazardClass` 1-3, `validityEnd ≥ validityStart` (aksi → `400 VALIDATION_FAILED`).
 * Yeni izin `status = Draft`.
 */
/**
 * İzin kaydetme kipi — SPEC §2 / brief 093 §2 (`CreateWorkPermitRequest.saveMode`). `draft` →
 * izin `Draft` kalır (yalnız kaydet). `submit` → açılışta onaya gönder: açan **reviewer**
 * (`permits/approve` grant'ı) ise backend **oto-onaylar** (`AwaitingSupplierInfo`), değilse
 * `Submitted` (ISG onayı bekler). Kararı backend verir — FE yalnız kipi bildirir; dönen
 * `status` otoritedir. Verilmezse backend `draft` varsayar (mevcut davranış korunur).
 */
export type WorkPermitSaveMode = 'draft' | 'submit';

export interface PermitCreateRequest {
  contractorUuid: string;
  /** İznin kapsayacağı sahaların public kimlikleri (**çoklu lokasyon — §14 Faz 2**; boş/atlanabilir). */
  siteUuids?: string[];
  /** İznin firma-tanımlı tipi (§22; aktif olmalı). */
  permitTypeUuid: string;
  title: string;
  description?: string | null;
  hazardClass: HazardClass;
  /** UTC ISO (§4; DateTime). */
  validityStart: string;
  /** UTC ISO (§4; DateTime). */
  validityEnd: string;
  notes?: string | null;
  /** Gerekli eğitimlerin (`Training`) public kimlikleri — her biri aynı firmada + aktif (§17.2). */
  trainingUuids?: string[];
  /**
   * İzinde araç olacak mı? — "araç olacak mı?" form bayrağı (§23 Faz 3a; verilmezse `false`).
   * Araçlar sonradan `POST /permits/{uuid}/vehicles` (§23) ile eklenir.
   */
  hasVehicles?: boolean;
  /**
   * Kaydetme kipi (SPEC §2 / brief 093 §2). `draft` → Draft; `submit` → onaya gönder (oto-onay
   * veya Submitted, açanın reviewer'lığına göre backend karar verir). Atlanırsa `draft`.
   */
  saveMode?: WorkPermitSaveMode;
}

/**
 * İzin güncelleme isteği — api-contract §14.1 (`PUT /permits/{uuid}`). Yalnız `Draft`/
 * `Submitted` düzenlenebilir (aksi → `409 PERMIT_NOT_EDITABLE`). `contractorUuid`
 * **değiştirilemez** (istekte yok). `siteUuids` iznin saha kümesini **replace** eder
 * (**çoklu lokasyon — §14 Faz 2**): boş/atlanan liste tüm sahaları temizler; verilen her saha
 * aynı firmada olmalı (herhangi biri yabancı → `404 SITE_NOT_FOUND`; **kısmi yazma yok**). Personel
 * kapsamı PUT ile değişmez — §14.3 uçlarıyla yönetilir. `permitTypeUuid` aktif + aynı firmada
 * olmalı (yok/pasif → `404 PERMIT_TYPE_NOT_FOUND`) — tip değiştirilebilir.
 */
export interface PermitUpdateRequest {
  /** İznin saha kümesini **replace** eder (**çoklu lokasyon — §14 Faz 2**; boş/atlanan → tüm sahalar temizlenir). */
  siteUuids?: string[];
  /** İznin firma-tanımlı tipi (§22; aktif olmalı). */
  permitTypeUuid: string;
  title: string;
  description?: string | null;
  hazardClass: HazardClass;
  validityStart: string;
  validityEnd: string;
  notes?: string | null;
  /** İzinde araç olacak mı? — "araç olacak mı?" form bayrağı (§23 Faz 3a). */
  hasVehicles?: boolean;
}

/**
 * İzin liste/filtre parametreleri — api-contract §14.1 (`GET /permits`) + §3 paging + §34.4
 * (S34 genişlemesi, additive/geriye dönük uyumlu). `status` (türetilmiş `Expired` dahil;
 * case-insensitive) **tekrarlanabilir** (§34.4 YENİ — `string[]` verilirse `?status=Active&
 * status=AwaitingEntryPermit` olarak OR'lanır; tanınmayanlar tek tek atlanır, hiçbiri
 * tanınmazsa süzgeç uygulanmaz). `contractorUuid`, `siteUuid` (iznin **herhangi** bir
 * sahasıyla eşleşme — çoklu lokasyon §14 Faz 2; tek saha ile filtrelenir), `permitTypeUuid`
 * (firma içinde çözülür; yok → boş sonuç), `from`/`to` (geçerlilik PENCERESİNİN kesişimi —
 * UTC ISO; "N gün içinde biten" sorusunu İFADE EDEMEZ, §34.4 notu), `validityEndFrom`/
 * `validityEndTo` (§34.4 YENİ — yalnız `validityEnd` üzerinde kapsayıcı aralık; §34.1
 * `expiringWindow`'un drill-through'u BUNU kullanır, `from`/`to`'yu DEĞİL), `search` (başlık
 * substring). Sıra backend'de geçerlilik başlangıcı azalan.
 */
export interface PermitListParams {
  /** Tek değer VEYA dizi (§34.4 YENİ — OR'lanır; `?status=Active&status=AwaitingEntryPermit`). */
  status?: PermitStatus | PermitStatus[];
  contractorUuid?: string;
  /** İznin **herhangi** bir sahasıyla eşleşme (matches-any; §14 Faz 2) — tek saha uuid ile filtre. */
  siteUuid?: string;
  permitTypeUuid?: string;
  /** Geçerlilik PENCERESİNİN alt sınırı (kesişim) — UTC ISO (§4). */
  from?: string;
  /** Geçerlilik PENCERESİNİN üst sınırı (kesişim) — UTC ISO (§4). */
  to?: string;
  /** Yalnız `validityEnd ≥` bu değer — UTC ISO (§4; §34.4 YENİ). */
  validityEndFrom?: string;
  /** Yalnız `validityEnd ≤` bu değer — UTC ISO (§4; §34.4 YENİ). */
  validityEndTo?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

/** Reddetme isteği — api-contract §14.2 (`POST /permits/{uuid}/reject`). `reason` zorunlu. */
export interface PermitRejectRequest {
  reason: string;
}

/**
 * AI incelemesi yeniden-tetik isteği — api-contract §31.3/§32 (`POST /permits/{uuid}/ai-review`).
 * `outcome` backend'de **yok sayılır** (yalnız wire geri-uyumluluğu için korunur — karar gerçek
 * incelemeden gelir, §32.2); `report` opsiyonel **reviewer notu**dur (Aksiyonlar'a SystemLog eklenir).
 */
export interface PermitAiReviewRequest {
  outcome: string;
  report?: string;
}

/** Personel kapsam ekleme isteği — api-contract §14.3 (`POST /permits/{uuid}/personnel`). */
export interface PermitPersonnelRequest {
  personnelUuids: string[];
}

/** Gerekli eğitim (replace) isteği — api-contract §17.2 (`POST /permits/{uuid}/trainings`). */
export interface PermitTrainingsRequest {
  trainingUuids: string[];
}

/**
 * Araç ekle/güncelle isteği — api-contract §23.1 (`POST|PUT /contractors/{uuid}/vehicles[/{vehicleUuid}]`, `POST /permits/{uuid}/vehicles`).
 * `plate` zorunlu (boş → `400 VALIDATION_FAILED`); `description` opsiyonel.
 */
export interface VehicleWriteRequest {
  plate: string;
  description?: string | null;
}

/**
 * Araç evrakı reddetme isteği — api-contract §23.1
 * (`POST /contractors/{uuid}/vehicles/{vehicleUuid}/documents/{did}/reject`). `reason` zorunlu (boş → `400`).
 */
export interface VehicleDocumentRejectRequest {
  reason: string;
}

/**
 * Araç evrakı yükleme isteği — api-contract §23.2
 * (`POST …/vehicles/{vehicleUuid}/documents`; §23.1 ve §23.2). Taşeron Yetkilisi araca evrak
 * bağlar: `type` §27 kaydının aktif bir `vehicle` kodu (§27.6: `INVALID`/`INACTIVE` → `400`),
 * `documentUuid` §8 depoda **confirmed + aynı firma** bir dosya (yabancı/onaysız → `404
 * DOCUMENT_NOT_FOUND`).
 * `validFrom`/`validUntil`/`note` opsiyonel (UTC ISO — §4). Belge `Pending` başlar. §8 yükleme
 * (`documentsApi.uploadDocument`) `documentUuid`'i üretir; bu istek yalnız iş modülüne link'ler
 * (içerik sunucudan geçmez).
 */
export interface VehicleDocumentUploadRequest {
  /** `vehicle` entity'sinin AKTİF §27 kayıt kodu (pasif → `INACTIVE`, bilinmeyen → `INVALID`). */
  type: string;
  documentUuid: string;
  /** Geçerlilik başlangıcı — UTC ISO (§4); opsiyonel. */
  validFrom?: string | null;
  /** Geçerlilik bitişi — UTC ISO (§4); opsiyonel. */
  validUntil?: string | null;
  note?: string | null;
}

/** Boş/whitespace dizeyi `undefined`'a indirger (searchParams'ta atlanır). */
function nonEmpty(value: string | undefined): string | undefined {
  return value && value.trim().length > 0 ? value.trim() : undefined;
}

// ============================================================================
// 14.1 İzinler (CRUD)
// ============================================================================

/**
 * `GET /permits` (`list`) — firmanın iş izinleri (sayfalı + filtreli). Sıra geçerlilik
 * başlangıcı azalan (istemci sort göndermez). `Expired` durumu backend'de türetilir.
 */
export function listPermits(params: PermitListParams = {}) {
  return api.list<WorkPermitDto>('permits', {
    searchParams: {
      status: params.status,
      contractorUuid: params.contractorUuid,
      siteUuid: params.siteUuid,
      permitTypeUuid: params.permitTypeUuid,
      from: params.from,
      to: params.to,
      validityEndFrom: params.validityEndFrom,
      validityEndTo: params.validityEndTo,
      search: nonEmpty(params.search),
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

/**
 * `GET /permits/{uuid}` (`get`) — tek izin + gerekli eğitimler + kapsanan personel
 * (readiness ile). Yok/kapsam dışı → `404 PERMIT_NOT_FOUND`.
 */
export function getPermit(uuid: string) {
  return api.get<WorkPermitDetailDto>(`permits/${uuid}`);
}

/**
 * `POST /permits` (`create`) — izin oluşturur (`status = Draft`); yeni kimlik `value.uuid`
 * (201). Doğrulama hataları → `400 VALIDATION_FAILED`; taşeron/saha/eğitim yok → `404`.
 */
export function createPermit(body: PermitCreateRequest) {
  return api.post<WorkPermitDto>('permits', { json: body });
}

/**
 * `PUT /permits/{uuid}` (`update`) — izni günceller. Yalnız `Draft`/`Submitted` (aksi →
 * `409 PERMIT_NOT_EDITABLE`). Yok/kapsam dışı → `404 PERMIT_NOT_FOUND`.
 */
export function updatePermit(uuid: string, body: PermitUpdateRequest) {
  return api.put<WorkPermitDto>(`permits/${uuid}`, { json: body });
}

// ============================================================================
// 14.2 Yaşam döngüsü
// ============================================================================

/** `POST /permits/{uuid}/submit` (`update`) — `Draft` → `Submitted` (aksi → `409 PERMIT_INVALID_STATE`). */
export function submitPermit(uuid: string) {
  return api.post<WorkPermitDto>(`permits/${uuid}/submit`);
}

/**
 * `POST /permits/{uuid}/approve` (`update`) — İSG uzmanı onayı. `Submitted` → `Approved`
 * (geçerlilik penceresi içindeyse doğrudan `Active` — backend türetir). Aksi →
 * `409 PERMIT_INVALID_STATE`.
 */
export function approvePermit(uuid: string) {
  return api.post<WorkPermitDto>(`permits/${uuid}/approve`);
}

/**
 * `POST /permits/{uuid}/reject` (`update`) — açık izni (`Draft`/`Submitted`) `Rejected`
 * yapar. `reason` zorunlu (boş → `400 VALIDATION_FAILED`); terminal/onaylı → `409`.
 */
export function rejectPermit(uuid: string, body: PermitRejectRequest) {
  return api.post<WorkPermitDto>(`permits/${uuid}/reject`, { json: body });
}

/** `POST /permits/{uuid}/close` (`update`) — izni `Closed` yapar (zaten Closed/Rejected → `409`). */
export function closePermit(uuid: string) {
  return api.post<WorkPermitDto>(`permits/${uuid}/close`);
}

/**
 * `POST /permits/{uuid}/ai-review` (`approve`) — reviewer'ın AI incelemesini **manuel yeniden-
 * tetiği** (yalnız `AwaitingAiReview` → `DepartmentReview`/`Revision`; §31.3/§32). `outcome` gerçek
 * incelemeden türetilir, istekteki değer yok sayılır — burada boş gönderilir. Aksi statü →
 * `409 PERMIT_INVALID_STATE`.
 */
export function rerunAiReview(uuid: string, report?: string) {
  return api.post<WorkPermitDto>(`permits/${uuid}/ai-review`, {
    json: { outcome: '', report } satisfies PermitAiReviewRequest,
  });
}

/**
 * `POST /permits/{uuid}/issue-entry-permits` (`approve`) — İSG son onayı: giriş belgelerini üretir
 * (yalnız `AwaitingEntryPermit` → `Active`, §33.1). Başka statü (Active dahil) →
 * `409 PERMIT_INVALID_STATE`; ikinci çağrı da aynı şekilde 409 (idempotent kapı).
 */
export function issueEntryPermits(uuid: string) {
  return api.post<WorkPermitDto>(`permits/${uuid}/issue-entry-permits`);
}

// ============================================================================
// 25.3 Kontrol Checklist İşaretleme (İSG uzmanı — tenant-scoped; `permits` modülü)
// ============================================================================
//
// KAYNAK: api-contract.md §25.3. İzin detayındaki (`WorkPermitDetailDto.controlItems`) bir kontrol
// itemını İSG uzmanı işaretler/kaldırır (+ opsiyonel not). Upsert (aynı izin,item için tek satır);
// `controlItemUuid` iznin permit tipinde olmalı. Başarı sonrası izin detayı (`GET /permits/{uuid}`)
// invalidate edilmelidir (çağıran katman — checkedByName/checkedAt/isChecked yeniden çözülür).

/**
 * `POST /permits/{uuid}/control-checks` (`update`, `permits` modülü) — bir kontrol itemını işaretler/
 * kaldırır (+ opsiyonel not). **Upsert** (idempotent). `controlItemUuid` iznin permit tipinde değil →
 * `404 CONTROL_ITEM_NOT_FOUND`; izin yok → `404 PERMIT_NOT_FOUND`. → güncel satır
 * (`WorkPermitControlItemDto`).
 */
export function setControlCheck(uuid: string, body: ControlCheckRequest) {
  return api.post<WorkPermitControlItemDto>(`permits/${uuid}/control-checks`, { json: body });
}

// ============================================================================
// 14.3 Personel Kapsamı
// ============================================================================

/**
 * `POST /permits/{uuid}/personnel` (`update`) — kapsanan personel ekler (idempotent;
 * her `personnelUuid` iznin taşeronuna + firmaya atanmış olmalı — değilse `404
 * PERSONNEL_NOT_FOUND`, kısmi yazma yok). Boş liste → `400 VALIDATION_FAILED`. → güncel liste.
 */
export function addPermitPersonnel(uuid: string, body: PermitPersonnelRequest) {
  return api.post<WorkPermitPersonnelDto[]>(`permits/${uuid}/personnel`, { json: body });
}

/**
 * `DELETE /permits/{uuid}/personnel/{userUuid}` (`update`) — personeli kapsamdan çıkarır
 * (soft-delete bağ; `userUuid` = `SupplierUser` public kimliği). Kapsamda değil → `404
 * PERSONNEL_NOT_FOUND`.
 */
export function removePermitPersonnel(uuid: string, userUuid: string) {
  return api.delete<Empty>(`permits/${uuid}/personnel/${userUuid}`);
}

// ============================================================================
// 17.2 Giriş İzni Kapısı (grant/revoke-entry)
// ============================================================================
//
// KAYNAK: api-contract.md §17.2. Kapsanan bir personele **saha giriş izni** verme/geri alma.
// İSG uzmanı personelin belgeleri (§15.4) + eğitimleri (§17) tam (`ready`) olduğunda "Giriş
// İzni Ver"i tıklar; readiness kapısı **sunucu otoritesindedir** (istemci yalnız `ready`
// değilken butonu disabled ederek 409'u önler). Başarıda güncel personel satırı döner.

/**
 * `POST /permits/{uuid}/personnel/{userUuid}/grant-entry` (`update`) — personele giriş izni
 * verir. **Yalnız `ready` ise** (`requiredDocumentsSatisfied` [iznin tipinde istenen tüm belge
 * türleri present + `Approved`; §22] VE tüm gerekli eğitimler geçildi); değilse
 * `409 PERSONNEL_NOT_READY` (giriş verilmez). Başarıda
 * `entryPermittedAt`/`entryPermittedByName` set. İzin yok → `404 PERMIT_NOT_FOUND`; personel
 * kapsamda değil → `404 PERSONNEL_NOT_FOUND`. → güncel satır.
 */
export function grantPermitEntry(uuid: string, userUuid: string) {
  return api.post<WorkPermitPersonnelDto>(`permits/${uuid}/personnel/${userUuid}/grant-entry`);
}

/**
 * `POST /permits/{uuid}/personnel/{userUuid}/revoke-entry` (`update`) — giriş iznini **geri
 * alır** (`entryPermittedAt`/`entryPermittedByName` temizlenir). **İdempotent** (verilmemiş
 * olsa da temiz durumu döner; readiness kapısı yok). İzin/personel yok → ilgili `404`.
 * → güncel satır.
 */
export function revokePermitEntry(uuid: string, userUuid: string) {
  return api.post<WorkPermitPersonnelDto>(`permits/${uuid}/personnel/${userUuid}/revoke-entry`);
}

// ============================================================================
// 17.2 Gerekli Eğitim (önkoşul) ataması
// ============================================================================

/**
 * `POST /permits/{uuid}/trainings` (`update`) — izne gerekli eğitimleri **replace** eder
 * (boş liste temizler). Her `trainingUuid` aynı firmada + aktif olmalı (değilse `404
 * TRAINING_NOT_FOUND`, kısmi atama yok). → güncel gerekli eğitim listesi.
 */
export function setPermitTrainings(uuid: string, body: PermitTrainingsRequest) {
  return api.post<RequiredTrainingDto[]>(`permits/${uuid}/trainings`, { json: body });
}

/**
 * `DELETE /permits/{uuid}/trainings/{trainingUuid}` (`update`) — bir gerekli eğitimi
 * kaldırır. Gerekli sette değil → `404 TRAINING_NOT_FOUND`.
 */
export function removePermitTraining(uuid: string, trainingUuid: string) {
  return api.delete<Empty>(`permits/${uuid}/trainings/${trainingUuid}`);
}

// ============================================================================
// 23.3 İzin araçları — BAĞ (ana firma) — api-contract §23.3
// ============================================================================
//
// İzin aracı KOPYALAMAZ, BAĞLAR. `vehicleUuid` her yerde KAYIT aracıdır (§23.0.1). Araç kaydı/belge/
// onay uçları `vehiclesApi`'dedir (`/contractors/{uuid}/vehicles…`, §23.1) — izin ekranı iznin
// `contractorUuid`'iyle onları çağırır.

/**
 * `POST /permits/{uuid}/vehicles` (`update`) — plaka iznin taşeronunun kaydında anahtarla aranır:
 * varsa araç yeniden kullanılır (`description` yok sayılır), yoksa oluşturulur; sonra izne bağlanır.
 * Zaten bağlıysa idempotent (`200`); yeni bağ `201`. İzin yok → `404 PERMIT_NOT_FOUND`.
 */
export function addPermitVehicle(uuid: string, body: VehicleWriteRequest) {
  return api.post<WorkPermitVehicleDto>(`permits/${uuid}/vehicles`, { json: body });
}

/**
 * `DELETE /permits/{uuid}/vehicles/{vehicleUuid}` (`update`) — aracı izinden ÇIKARIR (yalnız bağ
 * soft-delete; kayıt aracı ve belgeleri değişmez). Bağlı değil → `404 VEHICLE_NOT_FOUND`.
 */
export function removePermitVehicle(uuid: string, vehicleUuid: string) {
  return api.delete<Empty>(`permits/${uuid}/vehicles/${vehicleUuid}`);
}

// ============================================================================
// 14.4 Aksiyonlar = izin denetim izi (zaman-çizelgesi: sistem-log + yorum) — api-contract §35.1
// (§30.5/brief 093 §5'in YERİNE geçer)
// ============================================================================
//
// KAYNAK: api-contract §35.1 (canonical — S35, sahip onaylı). İzne bağlı kronolojik akış:
// **SystemLog** (otomatik — durum geçişi, bilgi değişikliği [ChangeSet], KKD/kontrol/ek belge
// talebi ekle-sil) + **Comment** (kişi yazar). Yazma yetkisi: açan + reviewer'lar.
//
// **⛔ ŞEKİL DÜZELTMESİ (S35, §35.1'in "ŞEKİL HATASI" notu).** `authorUuid` alanı KALDIRILDI —
// böyle bir kolon backend'de HİÇ olmadı (yalan tip; `packages/shared`'daki eski bildirim §30.5
// ile UYUŞMUYORDU). `changeSet` zaten dizi olarak bildirilmişti (bu haliyle KORUNUR) — S35
// ÖNCESİ backend'in kendisi bunu ham JSON METİN olarak dönüyordu (§30.5), yani eski tip aslında
// backend henüz projekte ETMEDEN önce "doğru" şekli VARSAYIYORDU (`PermitActivityPanel.tsx`'in
// `changeSet.map(...)` çağrısı bilgi değişikliği olan HER izinde `TypeError` ile çöküyordu — dev
// DB'de doğrulandı). S35 backend'i artık GERÇEKTEN bu diziyi projekte eder; tip ile canlı veri
// ARTIK eşleşir.

/**
 * Aksiyon kaydı türü — api-contract §35.1 (`WorkPermitActivity.Kind`, PascalCase wire).
 * `SystemLog` = sistemin otomatik ürettiği kayıt (geçiş/değişiklik); `Comment` = kişinin
 * yazdığı serbest metin.
 */
export type WorkPermitActivityKind = 'SystemLog' | 'Comment';

/**
 * Aksiyon kaydının aktörü — api-contract §35.1 (`WorkPermitActivityDto.actorType`, YENİ). HER
 * satırda dolu — S35 öncesi (`event = null`) satırlar bile okuma anında bir aktöre PROJEKTE
 * edilir (backfill DEĞİL): `AuthorUserId` dolu → `User`; `authorName = 'AI'` → `Ai`;
 * `authorName = 'Tedarikçi'` → `Supplier`; aksi `System`.
 */
export type WorkPermitActivityActorType = 'User' | 'Supplier' | 'System' | 'Ai';

/**
 * Aksiyon kaydı olayı — api-contract §35.1 (`WorkPermitActivityEvent`, YENİ). `SystemLog`
 * satırlarında dolu; `Comment`'te ve S35 öncesi yazılmış satırlarda `null` (geriye doldurma YOK).
 * Durum geçişi olayları (`fromStatus`/`toStatus` dolu) ile durum DEĞİŞTİRMEYEN olaylar (ikisi de
 * `null`) karışıktır — ayrım §35.1'in normatif tablosunda. **Her değer bir i18n etiketi taşımak
 * ZORUNDADIR** (`permitActivityEventLabelKeys`, `permitEnums.ts`) — o harita
 * `Record<WorkPermitActivityEvent, string>` olduğundan yeni bir olay eklenip haritaya
 * eklenmezse TS DERLEME HATASI verir (kaçırılan bir label riski YOK).
 */
export type WorkPermitActivityEvent =
  // ---- durum geçişleri (fromStatus/toStatus DOLU) ----
  | 'Created' // null → Draft — actor User
  | 'Submitted' // Draft → Submitted — actor User
  | 'AutoApproved' // Draft → AwaitingSupplierInfo — actor User (açan reviewer)
  | 'Approved' // Submitted → AwaitingSupplierInfo — actor User
  | 'Rejected' // Submitted → Rejected — actor User; reason = gerekçe (zorunlu)
  | 'SupplierSubmitted' // AwaitingSupplierInfo → AwaitingAiReview — actor Supplier
  | 'AiRevisionRequested' // AwaitingAiReview → Revision — actor Ai; reason = AI bulgu raporu
  | 'DepartmentReviewStarted' // AwaitingAiReview → DepartmentReview — actor Ai; departments = gerekli küme
  | 'DepartmentRejected' // DepartmentReview → Revision — actor User; departments = [reddeden]; reason = not
  | 'DepartmentReviewCompleted' // DepartmentReview → AwaitingEntryPermit — actor User (son onayı veren)
  | 'EntryPermitsIssued' // AwaitingEntryPermit → Active — actor User
  | 'Closed' // <açık durum> → Closed — actor User
  | 'Expired' // <Approved|Active> → Expired — actor System, TÜRETİLMİŞ (saklanmaz)
  // ---- durum değiştirmeyen olaylar (fromStatus/toStatus null) ----
  | 'AiReviewCompleted' // AI incelemesi geçti — actor Ai; reason = rapor
  | 'AiReviewFailed' // teknik hata, izin AwaitingAiReview'da kaldı — actor System
  | 'DepartmentApproved' // departments = [onaylayan] — actor User
  | 'DepartmentEscalated' // departments = [kimden, kime] — actor System
  | 'EntryGranted' // subject = kişi — actor User
  | 'EntryRevoked' // subject = kişi — actor User
  | 'PersonnelAssigned' // subject = kişi — actor User | Supplier
  | 'PersonnelUnassigned' // subject = kişi — actor User | Supplier
  | 'InfoUpdated' // changeSet dolu — actor User
  | 'ProtectiveEquipmentAdded' // text = öge adı
  | 'ProtectiveEquipmentRemoved' // text = öge adı
  | 'ControlItemAdded' // text = madde metni
  | 'ControlItemRemoved' // text = madde metni
  | 'DocumentRequestAdded' // text = belge türü adı (§30.10)
  | 'DocumentRequestRemoved'; // text = belge türü adı (§30.10)

/**
 * Kişi-hedefli bir olayın hedefi — api-contract §35.1 (`subject`, YENİ). Giriş izni ver/geri al
 * ve personel kapsama ekle/çıkar olaylarında dolu; aksi `null`. `supplierUserUuid` =
 * `SupplierUser.Uuid` (§14 `WorkPermitPersonnelDto.uuid` ile AYNI — bağ satırının uuid'i DEĞİL).
 * `name` yazma anı SNAPSHOT'ıdır (KVKK — yalnız ad; TC/telefon/e-posta YAZILMAZ).
 */
export interface WorkPermitActivitySubject {
  supplierUserUuid: string;
  name: string;
}

/**
 * Bir alan değişikliği (SystemLog `ChangeSet` satırı) — api-contract §35.1 (jsonb "eski/yeni",
 * `InfoUpdated` olayında dolu). `field` değişen alanın makine adı (FE i18n ile
 * `permits.field.<field>`'e eşler; eşleşme yoksa ham ad gösterilir). `oldValue`/`newValue`
 * okunur (stringleştirilmiş) değerler; alan yeni set/temizlendiyse biri `null` olabilir.
 */
export interface WorkPermitActivityChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

/**
 * Aksiyon kaydı — api-contract §35.1 (`WorkPermitActivityDto`, `GET /permits/{uuid}/activities`).
 * Sıralı iç `Id` **sızmaz** (yalnız `uuid`). `authorName` yazan kişi (`User`/`Supplier`'da "Ad
 * Soyad" snapshot'ı; `System`/`Ai`'de `null`, istemci sabit bir i18n etiketi basar). `text`
 * DEĞİŞMEDİ — `event` doluysa yalnız *Added/*Removed olaylarının öge adı taşıyıcısıdır (satır
 * kendi başlığını `event`den kurar); `event = null` satırlarda (yorum + S35 öncesi log) İNSAN
 * OKUNUR özet/yorum metnidir ve OLDUĞU GİBİ (§6 `Accept-Language`'a göre yerelleştirilmeden)
 * gösterilir — kullanıcı/model türevli bir dize, ASLA `dangerouslySetInnerHTML` ile değil düz
 * metin olarak render edilir. `changeSet` yalnız `InfoUpdated`'da dolu; aksi `null`. `at` kaydın
 * UTC ISO anı (§4).
 */
export interface WorkPermitActivityDto {
  uuid: string;
  kind: WorkPermitActivityKind;
  /** `SystemLog`'da dolu; `Comment`'te ve S35 öncesi satırlarda `null` (§35.1). */
  event: WorkPermitActivityEvent | null;
  /** HER satırda dolu (§35.1 — eski satırlar okuma anında projekte edilir, bkz. tip yorumu). */
  actorType: WorkPermitActivityActorType;
  authorName: string | null;
  /** Yalnız durum değiştiren olaylarda dolu (§35.1; `PermitStatus` ile aynı wire). */
  fromStatus: PermitStatus | null;
  toStatus: PermitStatus | null;
  /** Ret gerekçesi / departman ret notu / AI bulgu raporu — yoksa `null` (§35.1, ≤ 4000 karakter,
   * kullanıcı/model türevli serbest metin — düz metin olarak render edilir, HTML değil). */
  reason: string | null;
  /** Departman olaylarında dolu (§31 `PermitApprovalDepartment`); aksi `null` (§35.1). */
  departments: PermitApprovalDepartment[] | null;
  /** Kişi-hedefli olaylarda dolu (§35.1); aksi `null`. */
  subject: WorkPermitActivitySubject | null;
  text: string;
  /** `InfoUpdated`'da dolu alan bazlı eski→yeni; aksi `null` (§35.1). */
  changeSet: WorkPermitActivityChange[] | null;
  /** Kaydın anı — UTC ISO (§4; kronolojik sıralama backend'de artan; sentetik `Expired` satırı
   * `validityEnd`). */
  at: string;
}

/** Yorum ekleme isteği — brief 093 §5 (`POST /permits/{uuid}/activities`). `text` zorunlu. */
export interface WorkPermitActivityCreateRequest {
  text: string;
}

/**
 * `GET /permits/{uuid}/activities` (`get`) — iznin aksiyon akışı (kronolojik: sistem-log +
 * yorum birlikte). İzin yok/kapsam dışı → `404 PERMIT_NOT_FOUND`. (brief 093 §5.)
 */
export function listPermitActivities(uuid: string) {
  return api.get<WorkPermitActivityDto[]>(`permits/${uuid}/activities`);
}

/**
 * `POST /permits/{uuid}/activities` (`update`) — izne **yorum** ekler (`Comment`). Yetki:
 * açan + reviewer (aksi `403`). `text` zorunlu (boş → `400 VALIDATION_FAILED`). → oluşan kayıt.
 * (brief 093 §5.)
 */
export function addPermitActivity(uuid: string, body: WorkPermitActivityCreateRequest) {
  return api.post<WorkPermitActivityDto>(`permits/${uuid}/activities`, { json: body });
}

// ============================================================================
// 14.5 KKD + Kontrol itemı snapshot ekle/sil (İSG uzmanı) — SPEC §2.1 / brief 093 §6
// ============================================================================
//
// KAYNAK: SPEC §2.1 + brief 093 §6. İzin açılışında tipteki KKD + kontrol itemları izne
// KOPYALANIR (snapshot — per-permit değer kopyası, tip FK değil). İSG uzmanı bu izin-kopyasına
// **her aşamada ekleme/silme** yapar (tipten bağımsız); her ekle/sil → Aksiyonlar SystemLog.
// Okuma tarafı mevcut `GET /permits/{uuid}` (`requiredProtectiveEquipment` + `controlItems`)
// üzerinden döner (snapshot değerleri).
//
// **Sözleşme durumu (PM'e flag).** brief 093 §6 "ekle/sil uçları" der ama api-contract.md'de
// yol ADI HENÜZ YOK (backend 093 paralel). Aşağıdaki yollar mevcut §25.3 (`control-checks`) /
// §24 (`protective-equipment`) desenine göre **REST-tutarlı tahmindir**; backend kanonu
// yayınlanınca hizalanır. Uç `404` dönerse iş bozulmaz (PM'e bildirilir) — uydurma veri yok.

/** İzne kontrol maddesi ekleme isteği — snapshot'a serbest metin madde (SPEC §2.1). */
export interface PermitControlItemCreateRequest {
  text: string;
}

/**
 * `POST /permits/{uuid}/control-items` (`update`, `permits` — **sözleşme bekliyor, brief 093
 * §6**) — izin snapshot'ına yeni kontrol maddesi ekler (İSG uzmanı). → oluşan satır. İzin yok →
 * `404 PERMIT_NOT_FOUND`.
 */
export function addPermitControlItem(uuid: string, body: PermitControlItemCreateRequest) {
  return api.post<WorkPermitControlItemDto>(`permits/${uuid}/control-items`, { json: body });
}

/**
 * `DELETE /permits/{uuid}/control-items/{itemUuid}` (`update` — **sözleşme bekliyor, brief 093
 * §6**) — izin snapshot'ından bir kontrol maddesini kaldırır (İSG uzmanı). Madde yok →
 * `404 CONTROL_ITEM_NOT_FOUND`.
 */
export function removePermitControlItem(uuid: string, itemUuid: string) {
  return api.delete<Empty>(`permits/${uuid}/control-items/${itemUuid}`);
}

/**
 * İzne KKD ekleme isteği — snapshot'a serbest metin KKD **adı** (SPEC §2.1; api-contract §30.6).
 * Katalog FK değil — seçilen katalog ögesinin **adı** izne kopyalanır (snapshot; `control-items`
 * `{ text }` ile aynı desen).
 */
export interface PermitProtectiveEquipmentCreateRequest {
  /** Snapshot'a eklenecek KKD'nin adı — katalog ögesinin **adı** (değer kopyası, katalog `uuid` değil). */
  name: string;
}

/**
 * `POST /permits/{uuid}/protective-equipment` (`update`, api-contract §30.6) — izin KKD
 * snapshot'ına serbest metin KKD adı (`{ name }`) ekler (İSG uzmanı; kaynak genelde firma KKD
 * kataloğundaki bir ögenin adı). → güncel `ProtectiveEquipmentRefDto[]`. İzin yok → `404`.
 */
export function addPermitProtectiveEquipment(
  uuid: string,
  body: PermitProtectiveEquipmentCreateRequest,
) {
  return api.post<ProtectiveEquipmentRefDto[]>(`permits/${uuid}/protective-equipment`, {
    json: body,
  });
}

/**
 * `DELETE /permits/{uuid}/protective-equipment/{peUuid}` (`update` — **sözleşme bekliyor, brief
 * 093 §6**) — izin KKD snapshot'ından bir donanımı kaldırır (İSG uzmanı). Snapshot'ta yok →
 * `404 PROTECTIVE_EQUIPMENT_NOT_FOUND`.
 */
export function removePermitProtectiveEquipment(uuid: string, peUuid: string) {
  return api.delete<Empty>(`permits/${uuid}/protective-equipment/${peUuid}`);
}

// ============================================================================
// 30.10 İzne özel ek belge talebi (İSG uzmanı — tenant-scoped; `permits/update`)
// ============================================================================
//
// KAYNAK: api-contract.md §30.10. İznin permit tipinde istenen §22 standart belgelerine **EK**
// olarak, iznin detayından bir belge türü talep edilir. Talep **permit-seviyesidir** (izindeki TÜM
// personele uygulanır) ve readiness'e katılır (personel `missingDocumentTypes` birleşik kümeden —
// tek kaynak; bu yüzden ekle/kaldır başarısında izin detayı `GET /permits/{uuid}` invalidate edilir).
// Yetki + görünürlük KKD/kontrol ekle-sil (§30.6) ile birebir; her açık aşamada serbest.

/**
 * İzne ek belge talebi ekleme isteği — api-contract §30.10
 * (`POST /permits/{uuid}/document-requests`). `documentType` = `personnel` entity'sinin §27 kayıt
 * `code`'u (entity örtük); §27.6 doğrulaması `REQUIRED`/`INVALID`/`INACTIVE` → `400 VALIDATION_FAILED`
 * (alan `documentType`). `note` opsiyonel serbest açıklama (KVKK: yalnız tür + not — belge
 * içeriği/URL yok).
 */
export interface PermitDocumentRequestCreateRequest {
  documentType: string;
  note?: string | null;
}

/**
 * `POST /permits/{uuid}/document-requests` (`update`, `permits` modülü) — izne ek belge talebi ekler
 * (İSG uzmanı). `documentType` tanınmaz → `400 VALIDATION_FAILED` (alan `documentType`); aynı türün
 * aktif talebi varsa → `409 DUPLICATE_DOCUMENT_REQUEST`; izin yok/görünmez → `404 PERMIT_NOT_FOUND`.
 * → güncel `WorkPermitDocumentRequestDto[]`.
 */
export function addPermitDocumentRequest(uuid: string, body: PermitDocumentRequestCreateRequest) {
  return api.post<WorkPermitDocumentRequestDto[]>(`permits/${uuid}/document-requests`, {
    json: body,
  });
}

/**
 * `DELETE /permits/{uuid}/document-requests/{requestId}` (`update`, `permits` modülü) — ek belge
 * talebini kaldırır (soft-delete; `requestId` = `WorkPermitDocumentRequestDto.uuid`). Talep her açık
 * aşamada serbestçe kaldırılır (karşılanmış bir artefakt DEĞİL — izin-seviyesi gereksinim);
 * kaldırınca readiness yeniden hesaplanır. Talep iznin kümesinde değil → `404
 * DOCUMENT_REQUEST_NOT_FOUND`; izin yok → `404 PERMIT_NOT_FOUND`. → güncel
 * `WorkPermitDocumentRequestDto[]`.
 */
export function removePermitDocumentRequest(uuid: string, requestUuid: string) {
  return api.delete<WorkPermitDocumentRequestDto[]>(
    `permits/${uuid}/document-requests/${requestUuid}`,
  );
}
